import { useAnnotationImage } from "pages/LesionPage/hooks/useAnnotationImage";
import { makeStyles } from "@material-ui/styles";
import { useState, useEffect, useRef } from "react";
import { APITypesV1 as APITypes } from "@cur8/api-client";
import ImageLoadFailed from "render/components/ImageLoadFailed";
import LineThrobberBusyView from "render/ui/views/LineThrobberBusyView";

const useStyles = makeStyles({
  CanvasImage: {
    width: "95vw",
    maxWidth: "95%",
    margin: "6px",
  },
  DermaFlex: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: "100%",
  },
  CanvasImageFullscreen: {
    maxWidth: "100vw",
    maxHeight: "100vh",
    width: "auto",
    height: "auto",
    display: "block",
    margin: "auto",
  },
  ImageContainer: {
    display: "flex",
    margin: "4px",
    alignItems: "flex-start",
  },
  ImageContainerFullscreen: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100vw",
    height: "100vh",
    margin: "0",
    padding: "0",
    backgroundColor: "black",
    zIndex: 10000,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  Button: {
    margin: "0px",
  },
  ButtonFullscreen: {
    display: "none",
  },
  Timestamp: {
    fontSize: "12px",
  },
});

interface LargeCanvasImageProps {
  annotation: APITypes.Annotation;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

export function LargeCanvasImage({
  annotation: annotation,
  onFullscreenChange,
}: LargeCanvasImageProps) {
  const classes = useStyles();
  const { image, originalSize, failed } = useAnnotationImage(annotation, true);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [style, setStyle] = useState({
    isFullscreen: false,
    image: classes.CanvasImage,
    container: classes.ImageContainer,
    button: classes.Button,
  });

  function imageFullscreenToggle(e: React.MouseEvent) {
    e.stopPropagation();

    if (style.isFullscreen) {
      setStyle({
        isFullscreen: false,
        container: classes.ImageContainer,
        image: classes.CanvasImage,
        button: classes.Button,
      });
      onFullscreenChange?.(false);
    } else {
      setStyle({
        isFullscreen: true,
        container: classes.ImageContainerFullscreen,
        image: classes.CanvasImageFullscreen,
        button: classes.ButtonFullscreen,
      });
      onFullscreenChange?.(true);
    }
  }

  function parseTimestamp(anno: APITypes.Annotation): string {
    return anno.targetUri.split(":")[3].split(".")[0];
  }

  useEffect(() => {
    function drawAnnotation(
      canvasRect: DOMRect,
      ctx: CanvasRenderingContext2D
    ): void {
      if (!originalSize || !image) return;
      const scaleW = originalSize.w / image.width;
      const scaleH = originalSize.h / image.height;

      const r = (annotation.data as APITypes.AnnotationData2D).rect;
      const ss = ctx.strokeStyle;
      ctx.strokeStyle = "#bbffbb";
      ctx.lineWidth = 3;
      ctx.strokeRect(
        r.left / scaleW,
        r.top / scaleH,
        r.width / scaleW,
        r.height / scaleH
      );
      ctx.stroke();
      ctx.strokeStyle = ss;
    }

    if (canvasRef.current && image) {
      canvasRef.current.width = image.width;
      canvasRef.current.height = image.height;

      const context = canvasRef.current.getContext("2d");
      if (!context) return;

      context.drawImage(
        image,
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      );

      drawAnnotation(canvasRef.current.getBoundingClientRect(), context);
    }
  }, [image, annotation, originalSize]);

  if (failed) {
    return (
      <div>
        <ImageLoadFailed />
      </div>
    );
  }

  if (!image) {
    return (
      <div>
        <LineThrobberBusyView />
      </div>
    );
  }

  const timeStamp = !style.isFullscreen ? (
    <div className={classes.Timestamp}>{parseTimestamp(annotation)}</div>
  ) : undefined;

  const handleTouchStart = (e: React.TouchEvent) => {
    if (style.isFullscreen) {
      e.stopPropagation();
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (style.isFullscreen) {
      e.stopPropagation();
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (style.isFullscreen) {
      e.stopPropagation();
    }
  };

  return (
    <div
      className={style.container}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={classes.DermaFlex}>
        <canvas
          className={style.image}
          ref={canvasRef}
          onClick={imageFullscreenToggle}
          style={{ touchAction: style.isFullscreen ? "auto" : "none" }}
        ></canvas>
        {timeStamp}
      </div>
    </div>
  );
}
